import { useEffect, useMemo, useState } from "react";
import flatten from "lodash/flatten";
import isNumber from "lodash/isNumber";
import type { UseInfiniteQueryOptions } from "react-query";
import { useInfiniteQuery } from "react-query";
import { useWorkflowsApi } from "@/react/api";
import { WORKFLOW_RUN_STATUS } from "@/react/components/SettingsApp/Workflows/constants";
import type { Params } from "@/react/helpers/urlHelpers/toQueryString";
import type { Run } from "../generated/WorkflowsApi.types";

export const DEFAULT_PAGE_SIZE = 20 as const;
export const IN_PROGRESS_REFETCH_INTERVAL = 5000 as const;

const getRefetchInterval = (
  shouldDisableRefetching: boolean,
  areAllRunsCompleted: boolean,
) => {
  if (shouldDisableRefetching) return false;
  if (areAllRunsCompleted) return false;
  return IN_PROGRESS_REFETCH_INTERVAL;
};

// TODO: refactor all those types and make a more generic implementation for infinite query pagination
type QueryKeyType = readonly ["workflows", string, "runs", string, number];

interface UseRunsInfiniteQueryArgs {
  initialPage?: number;
  options?: Omit<
    UseInfiniteQueryOptions<
      WorkflowRunsPage,
      unknown,
      WorkflowRunsPage,
      WorkflowRunsPage,
      QueryKeyType
    >,
    "queryKey" | "queryFn"
  >;
  perPage?: number;
  query?: string;
  shouldDisableRefetching?: boolean;
  workflowId?: string;
}
interface Page<T = unknown> {
  has_next_page: boolean;
  page: number;
  records: T[];
}

interface WorkflowRunsPage extends Page<Run> {}

export const useRunsInfiniteQuery = (args: UseRunsInfiniteQueryArgs) => {
  const [areAllRunsCompleted, setAreAllRunsCompleted] = useState(false);
  const workflowsApi = useWorkflowsApi();

  const {
    query = "",
    workflowId = "",
    perPage = DEFAULT_PAGE_SIZE,
    initialPage = 1,
    shouldDisableRefetching = false,
    options = {
      refetchInterval: getRefetchInterval(
        shouldDisableRefetching,
        areAllRunsCompleted,
      ),
      enabled: !!workflowId,
    },
  } = args;

  const fetchPage = ({
    pageParam = initialPage,
  }): Promise<WorkflowRunsPage> => {
    let page = 1;

    if (isNumber(pageParam) && pageParam > 0) {
      page = pageParam;
    }

    const params: Params = {
      page,
      per_page: perPage,
    };

    if (query) {
      params.query = query;
    }

    return workflowsApi.fetchRuns({ workflowId, params });
  };

  const queryKey: QueryKeyType = [
    "workflows",
    workflowId,
    "runs",
    query,
    perPage,
  ] as const;

  const queryOptions: Omit<
    UseInfiniteQueryOptions<
      WorkflowRunsPage,
      unknown,
      WorkflowRunsPage,
      WorkflowRunsPage,
      QueryKeyType
    >,
    "queryKey" | "queryFn"
  > = {
    getNextPageParam: (lastPage: WorkflowRunsPage) =>
      lastPage.has_next_page ? lastPage.page + 1 : undefined,
    getPreviousPageParam: (firstPage: WorkflowRunsPage) =>
      firstPage.page > 1 ? firstPage.page - 1 : undefined,
    keepPreviousData: true,
    ...options,
  };

  const result = useInfiniteQuery(queryKey, fetchPage, queryOptions);
  const pages = useMemo(() => result.data?.pages || [], [result.data?.pages]);
  const runs = useMemo(() => flatten(pages.map(page => page.records)), [pages]);

  useEffect(() => {
    if (result.isLoading) {
      return;
    }

    const areAllRunsCompleted = runs.every(
      run => run?.status !== WORKFLOW_RUN_STATUS.IN_PROGRESS,
    );
    setAreAllRunsCompleted(areAllRunsCompleted);
  }, [runs, result.isLoading]);

  return {
    ...result,
    queryKey,
    runs,
    areAllRunsCompleted,
  };
};
